import { Injectable } from '@angular/core';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { createSelector, select, Store } from '@ngrx/store';
import { Map, OrderedSet } from 'immutable';
import { map, Observable } from 'rxjs';

import {
	CharacteristicType,
	DcTimestampFormat,
} from '../../core/dc-core-types';
import { DepotHeaderListEntity } from '../components/create-entity-from-depot-modal/depot-header-list.entity';
import { DepotItemEntity } from '../components/depot-list/depot-item.entity';
import { DepotListEntity } from '../components/depot-list/depot-list.entity';
import { DepotSchemaEntity } from '../components/depot-schema-viewer/depot-schema.entity';
import { SupportedLanguageEntity } from '../domain/supported-language.entity';
import { DepotOriginalSliceState, DepotSliceKey } from './depot.state';
import { DepotRootState, featureSelector } from './feature.store';

const baseSelector = createSelector(
	featureSelector,
	(state) => state[DepotSliceKey]
);

const listSelector = createSelector(baseSelector, (state) => state.list);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const depotById = (id: number) =>
	createSelector(listSelector, (list) => list.elements.get(`${id}`));

@Injectable()
export class DepotSelector {
	public constructor(private readonly store: Store<DepotRootState>) {}

	private get state(): Observable<DepotOriginalSliceState> {
		return this.store.pipe(select(baseSelector));
	}
	public getLoading$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('loading')));
	}

	public getDepotList$(): Observable<DepotListEntity> {
		return this.store.pipe(select(listSelector));
	}

	public includePublic$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('includePublicProjects')));
	}

	public getDepot$(id: number): Observable<DepotItemEntity | undefined> {
		return this.store.pipe(select(depotById(id)));
	}

	public getSelectedDepots$(
		selected: Array<string>
	): Observable<Array<DepotItemEntity>> {
		return this.getDepotList$().pipe(
			map((list) =>
				selected.reduce<Array<DepotItemEntity>>((acc, curr) => {
					const maybe = list.elements.get(`${curr}`);
					if (maybe) {
						acc.push(maybe);
						return acc;
					}
					return acc;
				}, [])
			)
		);
	}

	public getHeadersForSelectRepo$(): Observable<DepotHeaderListEntity> {
		return this.state.pipe(map((state) => state.headersForSelectRepo));
	}

	public getAvailableTags$(): Observable<OrderedSet<AppTagItemEntity>> {
		return this.state.pipe(map((state) => state.get('availableTags')));
	}

	public getDepotSchema$(): Observable<Array<DepotSchemaEntity> | null> {
		return this.state.pipe(map((state) => state.depotSchema));
	}

	public getAvailableCharacteristicTypes$(): Observable<
		Map<number, CharacteristicType>
	> {
		return this.state.pipe(
			map((state) => state.get('availableCharacteristicTypes'))
		);
	}

	public getAvailableDateFormats$(): Observable<
		Map<string, DcTimestampFormat>
	> {
		return this.state.pipe(map((state) => state.get('availableDateFormats')));
	}

	public getAvailableTimezones$(): Observable<Array<string>> {
		return this.state.pipe(map((state) => state.get('availableTimezones')));
	}

	public getSupportedLanguages$(): Observable<
		Map<string, SupportedLanguageEntity>
	> {
		return this.state.pipe(map((state) => state.get('supportedLanguages')));
	}
}
